// taskflow/frontend/src/visitor_ai/components/VisitorAIFullscreenAssistant.js
import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import "../styles/visitorAI.css";

import MissingInfoForm from "./MissingInfoForm";
import DisambiguationPanel from "./DisambiguationPanel";

/**
 * VisitorAIFullscreenAssistant (patched)
 *
 * Fixes:
 *  - prevents duplicate welcome message using hasWelcomedRef + messages.length check
 *  - safely renders message content when m.text is a plain object (avoids "Objects are not valid React child" error)
 *  - preserves all existing logic (missing info, disambiguation, confirm flows, TTS, etc.)
 */

function VisitorAIFullscreenAssistant({ onClose, pageContext = {} }) {
  const [query, setQuery] = useState("");
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]); // { sender: 'user'|'ai', text, meta? }
  const [lastResponse, setLastResponse] = useState(null); // raw backend response
  const [showMissingForm, setShowMissingForm] = useState(false);
  const [missingFields, setMissingFields] = useState([]); // array
  const [showDisambiguation, setShowDisambiguation] = useState(false);
  const [disambOptions, setDisambOptions] = useState([]);
  const [confirmAction, setConfirmAction] = useState(null); // action_schema
  const [autoPlayVoice, setAutoPlayVoice] = useState(true);

  const chatRef = useRef(null);

  // Prevent duplicate welcome messages across remounts/renders
  const hasWelcomedRef = useRef(false);

  // Helper: add message to chat
  const addMessage = (sender, text, meta = {}) => {
    // always push an immutable new object to ensure re-render
    setMessages((prev) => [...prev, { sender, text, meta }]);
  };

  // Auto-scroll to bottom when messages or loading change
  useEffect(() => {
    if (chatRef.current) {
      // small delay ensures new message height is measured
      try {
        chatRef.current.scrollTop = chatRef.current.scrollHeight + 200;
      } catch (e) {
        // ignore scrolling errors
      }
    }
  }, [messages, loading]);

  // Welcome message — only once when component first mounts and messages empty
  useEffect(() => {
    if (!hasWelcomedRef.current && messages.length === 0) {
      hasWelcomedRef.current = true;
      addMessage(
        "ai",
        "Hello 👋 — I’m Visitor AI! Ask me anything about visits, approvals, check-ins or reports."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once

  // Dummy STT start (placeholder)
  const startListening = () => {
    setListening(true);
    setTimeout(() => setListening(false), 1500);
  };

  // ----------------------------------------------------------------------------
  // Voice playback (Web Speech API) - speakText
  // ----------------------------------------------------------------------------
  const speakText = (text) => {
    if (!autoPlayVoice || !text) return;
    try {
      if ("speechSynthesis" in window) {
        const utter = new SpeechSynthesisUtterance(text);
        // conservative settings
        utter.rate = 1.0;
        utter.pitch = 1.0;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(utter);
      }
    } catch (e) {
      console.warn("TTS failed:", e);
    }
  };

  // ----------------------------------------------------------------------------
  // Utility: prettify field names for labels
  // ----------------------------------------------------------------------------
  const prettifyLabel = (k) => {
    if (!k) return k;
    return k
      .replace(/_/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .replace(/Id\b/, "ID");
  };

  // ----------------------------------------------------------------------------
  // Core: send query to backend
  // payload shape: { text, page_context }
  // ----------------------------------------------------------------------------
  const askAI = async (text, opts = {}) => {
    try {
      setLoading(true);
      const payload = {
        text: text,
        page_context: {
          ...(pageContext || {}),
          ...opts.page_context_extra, // allow caller to attach followup structured context
        },
      };

      console.log("📤 Sending AI payload:", payload);

      const res = await axiosClient.post("/ai/visitor/query", payload);
      console.log("📥 VisitorAI Response:", res?.data);

      const body = res?.data || {};
      setLastResponse(body);

      // Normalized friendly reply - prefer summary
      const aiReply =
        body?.data?.summary ||
        body?.data?.message ||
        (body?.status === "error" ? body?.message : null) ||
        (typeof body === "string" ? body : null) ||
        "No response received.";

      addMessage("ai", aiReply, { raw: body });

      // Play voice if possible
      // If structured has llm_message prefer that, else aiReply
      const voiceToPlay = body?.data?.llm_message || body?.data?.summary || aiReply;
      speakText(voiceToPlay);

      // Handle structured UI flows
      // 1) needs_info
      if (body?.status === "needs_info" || body?.data?.status === "needs_info") {
        // backend may return missing_fields either at top-level or inside data
        const payloadMissing =
          body?.missing_fields || body?.data?.missing_fields || body?.data?.errors || {};
        const normalized =
          body?.normalized_fields ||
          body?.data?.normalized_fields ||
          body?.data?.normalized ||
          {};
        // Build missing fields array for MissingInfoForm
        let fieldsArr = [];
        if (Array.isArray(payloadMissing)) {
          fieldsArr = payloadMissing.map((f) =>
            typeof f === "string" ? { key: f, label: prettifyLabel(f) } : f
          );
        } else if (typeof payloadMissing === "object" && payloadMissing !== null) {
          fieldsArr = Object.keys(payloadMissing).map((k) => ({
            key: k,
            label: prettifyLabel(k),
            reason: payloadMissing[k],
            prefill: normalized[k] || "",
          }));
        } else {
          fieldsArr = [{ key: "info", label: "More information" }];
        }

        setMissingFields(fieldsArr);
        setShowMissingForm(true);
        setShowDisambiguation(false);
        setConfirmAction(null);
        setLoading(false);
        return body;
      }

      // 2) disambiguation — backend expected to return data.disambiguation.options
      const disamb =
        body?.data?.disambiguation?.options || body?.data?.disambiguation_options || null;
      if (Array.isArray(disamb) && disamb.length > 0) {
        setDisambOptions(disamb);
        setShowDisambiguation(true);
        setShowMissingForm(false);
        setConfirmAction(null);
        setLoading(false);
        return body;
      }

      // 3) action_schema.confirm_required
      const actionSchema = body?.data?.action_schema || body?.action_schema || null;
      if (actionSchema && actionSchema.confirm_required) {
        setConfirmAction(actionSchema);
        setShowMissingForm(false);
        setShowDisambiguation(false);
        setLoading(false);
        return body;
      }

      // Otherwise clear special UI states
      setShowMissingForm(false);
      setShowDisambiguation(false);
      setConfirmAction(null);
      setLoading(false);
      return body;
    } catch (err) {
      console.error("AI Query Error:", err);
      setLoading(false);
      // Render server message if available
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.status ||
        "⚠️ Something went wrong. Please try again.";

      addMessage("ai", serverMsg);
      // keep existing special UIs untouched
      return { status: "error", message: serverMsg };
    }
  };

  // --------------------------------------------------------------------------
  // User submits missing info (from MissingInfoForm)
  // --------------------------------------------------------------------------
  const handleSubmitMissing = async (answers) => {
    // Close the form in UI immediately and show user's submission as a message
    const textSummary = Object.entries(answers)
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n");
    addMessage("user", textSummary);

    // Prepare followup context - backend orchestration will use this
    const page_context_extra = {
      previous_response: lastResponse,
      followup_missing_info: answers,
    };

    setShowMissingForm(false);
    setMissingFields([]);
    setLoading(true);

    await askAI(textSummary, { page_context_extra });
  };

  // --------------------------------------------------------------------------
  // User selects a disambiguation option
  // --------------------------------------------------------------------------
  const handleSelectDisambiguation = async (option) => {
    // Display selection in chat
    const text = option.name || option.value || JSON.stringify(option);
    addMessage("user", text);
    setShowDisambiguation(false);
    setDisambOptions([]);

    const page_context_extra = {
      previous_response: lastResponse,
      disambiguation_selection: option,
    };

    await askAI(`Selected: ${text}`, { page_context_extra });
  };

  // --------------------------------------------------------------------------
  // Confirmation (Approve / Cancel)
  // --------------------------------------------------------------------------
  const handleConfirm = async (approve = true) => {
    if (!confirmAction) return;
    const verb = approve ? "confirm" : "cancel";
    addMessage("user", `${verb} ${confirmAction.action_type || "action"}`);
    const page_context_extra = {
      previous_response: lastResponse,
      confirm_action: {
        ...confirmAction,
        approved: approve,
      },
    };
    setConfirmAction(null);
    setLoading(true);
    await askAI(`${verb} ${confirmAction.action_type || ""}`, {
      page_context_extra,
    });
  };

  // When user clicks ASK (main input)
  const handleAsk = () => {
    if (!query.trim()) return;
    addMessage("user", query);
    const q = query;
    setQuery("");
    askAI(q);
  };

  // Toggle voice autoplay
  const toggleAutoPlay = () => setAutoPlayVoice((p) => !p);

  // ----------------------------------------------------------------------------
  // Message renderer: safely render m.text of different types
  // ----------------------------------------------------------------------------
  const renderMessageContent = (m) => {
    const t = m?.text;

    // If React element already (rare), render it
    // use React.isValidElement (can't import React as alias, but it is in scope)
    // eslint-disable-next-line no-undef
    if (React.isValidElement && React.isValidElement(t)) {
      return t;
    }

    // Strings / numbers -> plain bubble text
    if (typeof t === "string" || typeof t === "number") {
      return t;
    }

    // Arrays -> join items (if primitives) or render each item
    if (Array.isArray(t)) {
      return (
        <div style={{ display: "block" }}>
          {t.map((item, idx) => (
            <div key={idx} style={{ marginBottom: 6, whiteSpace: "pre-wrap" }}>
              {typeof item === "object" ? (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(item, null, 2)}
                </pre>
              ) : (
                String(item)
              )}
            </div>
          ))}
        </div>
      );
    }

    // Plain object -> attempt friendly render if it matches missing-field shape
    if (typeof t === "object" && t !== null) {
      // If this object looks like a single missing field item { key, label, reason, prefill }
      if ("key" in t && ("label" in t || "reason" in t || "prefill" in t)) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontWeight: 700 }}>{t.label || prettifyLabel(t.key)}</div>
            {t.reason && (
              <div style={{ fontSize: 13, color: "#c0392b" }}>{String(t.reason)}</div>
            )}
            {("prefill" in t) && (
              <div style={{ fontSize: 13, color: "#333" }}>
                Prefill: {String(t.prefill ?? "")}
              </div>
            )}
          </div>
        );
      }

      // If object is map of fields (field: reason) — render entries
      const keys = Object.keys(t);
      if (keys.length > 0 && keys.every((k) => typeof t[k] !== "object")) {
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {keys.map((k) => (
              <div key={k}>
                <strong>{prettifyLabel(k)}:</strong>{" "}
                <span style={{ color: "#333" }}>{String(t[k])}</span>
              </div>
            ))}
          </div>
        );
      }

      // Fallback: pretty-print JSON
      return <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(t, null, 2)}</pre>;
    }

    // Any other fallback
    return String(t);
  };

  // --------------------------------------------------------------------------
  // JSX
  // --------------------------------------------------------------------------
  return (
    <div
      className="visitor-ai-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: "1000px",
          maxWidth: "92%",
          height: "80vh",
          borderRadius: "22px",
          background: "rgba(255, 255, 255, 0.95)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            width: "100%",
            padding: "14px 20px",
            background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18 }}>Visitor AI Assistant</div>

          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <label style={{ fontSize: 13, opacity: 0.95 }}>
              <input
                type="checkbox"
                checked={autoPlayVoice}
                onChange={toggleAutoPlay}
                style={{ marginRight: 6 }}
              />
              Voice
            </label>

            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.2)",
                color: "white",
                padding: "6px 12px",
                borderRadius: 10,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <X size={16} /> Close
            </button>
          </div>
        </div>

        {/* CHAT AREA */}
        <div
          ref={chatRef}
          style={{
            flex: 1,
            background: "#ffffff",
            padding: 20,
            overflowY: "auto",
          }}
        >
          {messages.map((m, index) => {
            const isUser = m.sender === "user";
            return (
              <div
                key={index}
                style={{
                  marginBottom: 14,
                  textAlign: isUser ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "10px 16px",
                    borderRadius: 14,
                    maxWidth: "75%",
                    background: isUser ? "linear-gradient(90deg,#6a11cb,#b721ff)" : "#f1f1f1",
                    color: isUser ? "#fff" : "#000",
                    fontSize: 15,
                    whiteSpace: "pre-wrap",
                    textAlign: "left",
                  }}
                >
                  {renderMessageContent(m)}
                </div>
              </div>
            );
          })}

          {loading && <div style={{ color: "#666" }}>Thinking…</div>}

          {/* Special UI panels inserted in chat area */}
          {showMissingForm && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  padding: 12,
                  borderRadius: 12,
                  border: "1px dashed #ddd",
                  background: "#fff9f0",
                }}
              >
                <strong>AI needs more information:</strong>
                <MissingInfoForm missingFields={missingFields} onSubmit={handleSubmitMissing} />
              </div>
            </div>
          )}

          {showDisambiguation && (
            <div style={{ marginTop: 12 }}>
              <DisambiguationPanel options={disambOptions} onSelect={handleSelectDisambiguation} />
            </div>
          )}

          {confirmAction && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 12,
                border: "1px solid #e6e6e6",
                background: "#f7fbff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <div style={{ fontWeight: 700 }}>Confirm: {confirmAction.action_type}</div>
                <div style={{ fontSize: 13, color: "#333", marginTop: 6 }}>
                  {confirmAction.params ? (
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(confirmAction.params, null, 2)}
                    </pre>
                  ) : (
                    "No details provided."
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => handleConfirm(false)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 10,
                    border: "1px solid #ccc",
                    background: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirm(true)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 10,
                    border: "none",
                    background: "linear-gradient(90deg,#10b981,#34d399)",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>

        {/* INPUT AREA */}
        <div
          style={{
            padding: 16,
            background: "#ffffff",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <input
            type="text"
            placeholder="Type or click Speak…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAsk();
            }}
            style={{
              flex: 1,
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid #ddd",
              fontSize: 15,
            }}
          />

          {/* SPEAK BUTTON */}
          <button
            onClick={startListening}
            disabled={listening}
            style={{
              padding: "8px 18px",
              borderRadius: 10,
              fontWeight: 600,
              color: "white",
              background: "linear-gradient(90deg, #6a11cb 0%, #8e2de2 40%, #b721ff 100%)",
              border: "none",
              cursor: "pointer",
              opacity: listening ? 0.6 : 1,
            }}
          >
            {listening ? "Listening…" : "Speak"}
          </button>

          {/* ASK BUTTON */}
          <button
            onClick={handleAsk}
            disabled={loading}
            style={{
              padding: "8px 20px",
              borderRadius: 10,
              fontWeight: 600,
              color: "white",
              background: "linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #a78bfa 100%)",
              border: "none",
              cursor: "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "Thinking…" : "Ask"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VisitorAIFullscreenAssistant;
